import * as React from "react";
import {cx}       from "emotion";

import "./tab-order-gallery.scss";

import {XdSentry}       from "@Common/libs/sentry/xd-sentry";
import {consumeContext} from "@Common/react/context";
import {ComponentLink,
        Link,
        makeChain}      from "@Common/react/cst2";
import {Load2}          from "@Common/react/load2";
import {UseRef}         from "@Common/react/use-ref";
import {UseState}       from "@Common/react/use-state";
import {Types}          from "@Common/types";
import {ConfirmDialog}  from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog";
import {UploadButton}   from "@Common/ui-components/upload-button/upload-button";

import {DialogEditImage}   from "../components/dialog-edit-image";
import {ErrorMessage}      from "../components/error-message";
import {GalleryImage}      from "../components/gallery-image";
import {LoadingSpinner}    from "../components/loading-spinner";
import {MultiSelectFooter} from "../components/multi-select-footer";
import {Paginator}         from "../components/paginator";
import {GetImageQuality}   from "../utils/get-image-quality";


export const TabOrderGallery = ({
    orderId,
    modal,
    viewType,
    numResultsPerPage,
    imageArea             = null,
    singleSelectOptions   = null,
    multiSelectOptions    = null,
    isMobile,
    disallowSvgImages     = false,
    onPaginateNumResults,
}:{
    orderId               : number,
    modal                 : Types.Common.UiComponents.ModalService3.ModalService3Next<Types.Common.UiComponents.PhotoDashboard.Models.PhotoDashboardData>,
    viewType              : Types.Common.UiComponents.PhotoDashboard.viewTypes,
    numResultsPerPage     : Types.Common.UiComponents.PhotoDashboard.numResultsPerPage,
    imageArea            ?: Types.Common.UiComponents.PhotoDashboard.Models.ImageArea,
    singleSelectOptions  ?: Types.Common.UiComponents.PhotoDashboard.Models.SingleSelectOptions,
    multiSelectOptions   ?: Types.Common.UiComponents.PhotoDashboard.Models.MultiSelectOptions,
    isMobile              : boolean,
    disallowSvgImages    ?: boolean,
    onPaginateNumResults  : Types.Common.UiComponents.PhotoDashboard.Functions.OnPaginateNumResults,
}) => (

    makeChain<JSX.Element>()

    .then(...consumeContext("apis"))
    .then(...consumeContext("company"))
    .then(...consumeContext("userLevels"))
    .then(...consumeContext("systemConstants"))
    .then(...consumeContext("templateOptions"))

    // set state vars
    .then("state", (_, next:ComponentLink<typeof UseState<{
        isImageLoadingToEditor  : boolean,
        uploadProgressPercent   : number,
        editingImageId          : number,
        showFooterUpload        : boolean,
        errorMessage            : string,
        pageNumber              : number,
    }>>) => UseState({
        defaultValue: {
            isImageLoadingToEditor: false,
            uploadProgressPercent: 0,
            editingImageId: 0,
            showFooterUpload: false,
            errorMessage: "",
            pageNumber: 1,
        },
        next,
    }))

    // display loader while applying images to the editor
    .then(({state}, next:Link<JSX.Element>) => {

        if (state.value.isImageLoadingToEditor) {
            return (
                <div className="tab-content order-gallery">
                    <div className="results">
                        <LoadingSpinner text="Applying Image"/>
                    </div>
                </div>
            );
        }

        return next();
    })

    // prepare loader for order images
    .then("loaderPhotos", ({apis}, next:ComponentLink<typeof Load2<Types.Apis.Models.GalleryImage[]>>) => Load2({
        _key: "images_" + orderId,
        fetch: () => apis.next.gallery.orders.getImages(orderId),
        captureException: true,
        next,
    }))

    // evaluate the max file size to be based from the number of pages
    .then("maxFileSize", ({userLevels, systemConstants, templateOptions}, next:Link<JSX.Element, number>) => {

        // set the base upload max from number of pages
        let maxFileSize = systemConstants.value.UPLOAD_MAX_PER_PAGE;
        if (templateOptions?.tempPrefOfNumOfPages) maxFileSize *= templateOptions.tempPrefOfNumOfPages;

        // if max is larger than chunked uploader allows, cap at the chunked upload max
        if (maxFileSize > systemConstants.value.UPLOAD_MAX_CHUNKED_UPLOAD)
            maxFileSize = systemConstants.value.UPLOAD_MAX_CHUNKED_UPLOAD;

        // if an internal user and the max is under the php max, set to php max
        if (userLevels.internalUser && maxFileSize < systemConstants.value.UPLOAD_MAX_PHP_FILE_SIZE)
            maxFileSize = systemConstants.value.UPLOAD_MAX_PHP_FILE_SIZE;

        return next(maxFileSize);

    })

    // build the upload method
    .then("onUpload", ({apis, company, state, loaderPhotos, maxFileSize, systemConstants}, next:Link<JSX.Element, Types.Common.UiComponents.PhotoDashboard.Functions.OnUpload>) => next(async (event) => {

        // clear the error message if one is set, and set progress percent to -1 to show processing status
        state.onChange({
            ...state.value,
            errorMessage: "",
            uploadProgressPercent: -1,
        });

        // issue error if problem capturing the file
        if (!event.target || !event.target.files.length || Array.from(event.target.files).find(x => !x.size)) {
            state.onChange({
                ...state.value,
                errorMessage: "There was an error applying your image selection. Please try again.",
                uploadProgressPercent: 0,
            });
            return;
        }

        // validate all files
        let allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
        if (!disallowSvgImages) allowedTypes.push("image/svg+xml");
        for (const file of event.target.files) {
            if (!allowedTypes.includes(file.type)) {
                const validationMessageImageName = (event.target.files.length > 1) ? `“${file.name}”` : "";
                state.onChange({
                    ...state.value,
                    errorMessage: `Your ${validationMessageImageName} is not supported. Please select a .jpg, .png${disallowSvgImages ? "" : ", .svg"} or .pdf file.`,
                    uploadProgressPercent: 0,
                });
                return;
            }
            if (file.size > maxFileSize) {
                const validationMessageImageName = (event.target.files.length > 1) ? `“${file.name}”` : "";
                state.onChange({
                    ...state.value,
                    errorMessage: `Your image ${validationMessageImageName} is too large. Please select an image less than ${Math.round(maxFileSize/1000000)}MB.`,
                    uploadProgressPercent: 0,
                });
                return;
            }
        }

        // find total payload to be uploaded for calculating progress percent
        let totalPayload = Array.from(event.target.files).reduce((total, file) => total + file.size, 0);
        let uploadedPayload = 0;

        // initialize variables used in process for warning of images skipped due to low quality settings
        const numFiles = event.target.files.length; // needed because event.target won't exist when the final error message is constructed
        let lowQualityFiles = [];

        // upload all files
        for (const file of event.target.files) {

            // if disabled low quality, skip upload of low quality photos
            if (!company.prefs.allowLowQualityPhotosProperty
                && GetImageQuality({
                    imageInfo: {
                        fileSize: file.size,
                        fileType: (file.type === "image/png") ? "png" : "",
                    },
                    photoQualitySettings: imageArea.photoQualitySettings
                }).isLowQuality
            ) {
                lowQualityFiles.push(file.name);
                totalPayload -= file.size;
                continue;
            }

            // upload the file
            let uploadedFile;
            try {
                uploadedFile = await apis.next.gallery.orders.upload({
                    orderId,
                    formData: {file},
                    postMaxSize: systemConstants.value.UPLOAD_MAX_PHP_FILE_SIZE,
                    onUploadProgress: (percent) => {
                        state.onChange({
                            ...state.value,
                            errorMessage: "", // previous error message that was already cleared is returned without this assignment
                            uploadProgressPercent: Math.ceil((uploadedPayload + (file.size * percent / 100)) / totalPayload * 100)
                        });
                    },
                });
            } catch (e) {}

            // issue an error if no response
            if (!uploadedFile) {
                XdSentry.addBreadcrumb({
                    category: "custcanv",
                    message: "User attempted to upload an image that failed during transmission.",
                    level: XdSentry.Severity.Error,
                    data: {
                        orderId,
                        uploadFileData: {file},
                    }
                });
                XdSentry.captureMessage("Image Upload Error", XdSentry.Severity.Log);
                state.onChange({
                    ...state.value,
                    errorMessage: "There was an error uploading your file.",
                });
                return;
            }

            // issue error if no path was returned
            if (!uploadedFile.printUrl) {
                XdSentry.addBreadcrumb({
                    category: "custcanv",
                    message: "User uploaded an image that that did not return a print URL.",
                    level: XdSentry.Severity.Error,
                    data: {
                        uploadFileData: file,
                        returnedData: uploadedFile,
                    }
                });
                XdSentry.captureMessage("Image Upload Error", XdSentry.Severity.Log);
                state.onChange({
                    ...state.value,
                    errorMessage: "There was an error uploading your file.",
                    uploadProgressPercent: 0,
                });
                return;
            }

            // add size to payload for calculating upload percent
            uploadedPayload += file.size;

            // auto-select the image if using multi-select
            if (multiSelectOptions) {
                multiSelectOptions.onSelect({
                    imageId: uploadedFile.id,
                    assetType: "order",
                    imagePath: uploadedFile.printUrl,
                    thumbnailUrl: uploadedFile.thumbnailUrl,
                });
            }

        }

        // construct an error message for any low quality images that were skipped due to company quality rules
        let errorMessage = "";
        if (lowQualityFiles.length) {
            if (numFiles === 1)
                errorMessage = "Your image was not uploaded because it does not meet the minimum quality standards for our printing process. Please select a larger image.";
            else if (numFiles === lowQualityFiles.length)
                errorMessage = "Your images were not uploaded because they do not meet the minimum quality standards for our printing process. Please select larger images.";
            else if (lowQualityFiles.length === 1)
                errorMessage = "The following file was not uploaded because it does not meet the minimum quality standards for our printing process: " + lowQualityFiles[0] + " Please select a larger image.";
            else
                errorMessage = "The following files were not uploaded because they do not meet the minimum quality standards for our printing process: " + lowQualityFiles.join(", ") + " Please select larger images.";
        }

        // reload the image results
        state.onChange({
            ...state.value,
            uploadProgressPercent: 0,
            errorMessage,
        });
        loaderPhotos.reload();

    }))

    // initialize delete and edit modals
    .then("dialogEditImage", ({state, apis, loaderPhotos}, next:ComponentLink<typeof DialogEditImage>) => DialogEditImage({
        next,
        images: loaderPhotos.value,
        imageId: state.value.editingImageId,
        orderId,
        onApplyImageEdit: async (imageId, imageName) => {
            const updatedImage = await apis.next.gallery.orders.update({orderId, imageId, postData: {name: imageName}});
            if (updatedImage.errorMessage) return updatedImage.errorMessage;
            loaderPhotos.reload();
        },
    }))
    .then("dialogConfirmDelete", (_, next:ComponentLink<typeof ConfirmDialog>) => ConfirmDialog({next}))

    // check if pagination is required
    .then("includePagination", ({loaderPhotos}, next:Link<JSX.Element, boolean>) => next(isMobile && !!loaderPhotos.value?.length))

    // render the images list
    .then("imagesContents", ({state, apis, company, loaderPhotos, includePagination,
        dialogConfirmDelete, dialogEditImage}, next:Link<JSX.Element>) => {

        if (loaderPhotos.loading) return next(<LoadingSpinner text="Loading Photos"/>);

        // retrieve the image data from the loader
        let images = loaderPhotos.value;
        if (includePagination) images = images.slice((state.value.pageNumber-1)*numResultsPerPage, state.value.pageNumber*numResultsPerPage);

        // return blank for empty galleries
        if (!images?.length) return next();

        // display the images when available
        return next(<>
            {images.map((image) => (
                <GalleryImage
                    key={image.id}
                    id={image.id}
                    imageName={image.name}
                    imageAttributionData={image.attribution}
                    thumbnailUrl={image.thumbnailUrl}
                    viewType={viewType}
                    fileType={image.fileType}
                    fileSize={image.fileSize}
                    width={image.width}
                    height={image.height}
                    areaWidth={imageArea?.widthInches}
                    areaHeight={imageArea?.heightInches}
                    photoQualitySettings={imageArea?.photoQualitySettings}
                    allowLowQuality={company.prefs.allowLowQualityPhotosProperty}
                    disabled={state.value.uploadProgressPercent > 0 || (image.fileType === "svg" && disallowSvgImages)}
                    selectedIndex={(!multiSelectOptions) ? 0 : multiSelectOptions.selectedImages.findIndex(x => x.assetType === "order" && x.imageId === image.id)+1}
                    onClick={() => {

                        // apply the image for single select mode
                        if (singleSelectOptions) {

                            // set the image as applying
                            state.onChange({
                                ...state.value,
                                isImageLoadingToEditor: true,
                            });

                            // apply the image
                            singleSelectOptions.onApply(
                                imageArea,
                                "order",
                                image.id,
                                image.printUrl,
                                modal.close
                            );

                        }

                        // select the image for multi-select mode
                        else if (multiSelectOptions) {

                            multiSelectOptions.onSelect({
                                assetType: "order",
                                imageId: image.id,
                                imagePath: image.printUrl,
                                thumbnailUrl: image.thumbnailUrl
                            });

                        }

                    }}
                    onEdit={async () => {
                        state.onChange({...state.value, editingImageId: image.id});
                        await dialogEditImage.show();
                    }}
                    onDelete={async () => {
                        // prompt delete confirmation
                        if (await dialogConfirmDelete.show({
                            actionName: "delete",
                            message: <span>Deleting <i>{image.name}</i>.</span>,
                            buttons: {
                                confirmLabel: "Yes",
                            },
                        })) {
                            await apis.next.gallery.orders.delete({orderId, imageId: image.id});
                            loaderPhotos.reload();
                        }
                    }}
                />
            ))}
        </>);
    })

    // create the results area ref hook for controlling scroll position
    .then("resultsAreaRef", (_, next:Link<JSX.Element, Types.Common.React.UseRef.UseRefNext>) => UseRef({next}))

    // create the upload button ref for checking if the button is out of view
    .then("uploadButtonRef", (_, next:Link<JSX.Element, Types.Common.React.UseRef.UseRefNext>) => UseRef({next}))

    // render the tab contents
    .then(({state, imagesContents, onUpload, loaderPhotos, maxFileSize, includePagination, resultsAreaRef, uploadButtonRef}) => (<>
        <div className={cx("tab-order-gallery-ptf tab-content order-gallery", "viewtype-"+viewType)}>
            <div className="results"
                ref={ref => resultsAreaRef.set(ref)}
                onScroll={(e) => {

                    if (!uploadButtonRef.get()) return;
                    const topPosition = resultsAreaRef.get().scrollTop;
                    const uploadButtonPosition = uploadButtonRef.get().offsetTop + uploadButtonRef.get().offsetHeight;

                    if (!state.value.showFooterUpload && topPosition > uploadButtonPosition) {
                        state.onChange({...state.value, showFooterUpload: true});
                    }
                    else if (state.value.showFooterUpload && topPosition <= uploadButtonPosition) {
                        state.onChange({...state.value, showFooterUpload: false});
                    }

                }}
            >
                <ErrorMessage text={state.value.errorMessage}/>
                <div className="source-images">
                    {!loaderPhotos.loading && (
                    <div className="card upload">
                        <h2>Upload a Photo</h2>
                        <p className="browse">
                            <UploadButton
                                buttonRef={uploadButtonRef}
                                onUpload={onUpload}
                                uploadProgressPercent={state.value.uploadProgressPercent}
                                allowMultipleFiles={true}
                            />
                        </p>
                        <p>
                            Images must be JPEG (.jpg, .jpeg, .jpe), PNG{disallowSvgImages ? "" : ", SVG"} or PDF format. Less than {Math.round(maxFileSize/1000000)}MB in file size.
                        </p>
                    </div>
                    )}
                    {imagesContents}
                </div>
            </div>
            {includePagination && (
                <Paginator
                    numItems={loaderPhotos.value?.length}
                    pageNumber={state.value.pageNumber}
                    itemsPerPage={numResultsPerPage}
                    onPaginateNumResults={numResults => {
                        resultsAreaRef.get().scrollTop = 0;
                        onPaginateNumResults(numResults);
                    }}
                    onPaginatePageNumber={pageNumber => {
                        resultsAreaRef.get().scrollTop = 0;
                        state.onChange({...state.value, pageNumber});
                    }}
                />
            )}
        </div>
        <div className={cx("footer upload", {hidden: !state.value.showFooterUpload})}>
            <UploadButton
                buttonText="Upload a Photo"
                onUpload={onUpload}
                uploadProgressPercent={state.value.uploadProgressPercent}
                allowMultipleFiles={true}
            />
            <p>Images must be JPEG (.jpg, .jpeg, .jpe), PNG{disallowSvgImages ? "" : ", SVG"} or PDF format. Less than {Math.round(maxFileSize/1000000)}MB in file size.</p>
        </div>
        {multiSelectOptions && (
        <MultiSelectFooter
            maxSelections={multiSelectOptions.maxSelections}
            numSelected={multiSelectOptions.selectedImages.length}
            onApply={() => {
                state.onChange({...state.value, isImageLoadingToEditor: true});
                multiSelectOptions.onApply(imageArea, multiSelectOptions.selectedImages, modal.close);
            }}
            onClear={multiSelectOptions.onClearSelections}
        />
        )}
    </>))

    .run()

);
